import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/api/errors';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { useTenantSwitch } from '~/hooks/useTenantSwitch';
import { useAuthStore } from '~/store/auth.store';
import type { AvailableTenant } from '~/store/auth.store';

import { useWrongSubsidiaryRedirect } from '../../hooks/use-wrong-subsidiary-redirect';

/** Stable empty reference — a fresh `[]` from a Zustand selector loops `useSyncExternalStore`. */
const EMPTY_TENANTS: AvailableTenant[] = [];

interface Props {
  experimentId: string;
  /** The error thrown by the experiment-detail query (typically an `ApiError`). */
  error: unknown;
}

/**
 * Build the post-switch landing target from the live URL so the deep link
 * (path + analytics search params) survives the subsidiary switch + reload.
 */
function currentRedirectTarget(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Error state for the experiment-detail page. Turns the old dead-end "not found"
 * into a subsidiary-aware recovery: auto-switches to the owning subsidiary when
 * the backend names one, offers a deep-link-preserving manual switch for any
 * other not-found/forbidden, and shows an honest generic message otherwise.
 */
export function ExperimentLoadError({ experimentId, error }: Props) {
  const { t } = useTranslation('experiments');
  const redirectTo = currentRedirectTarget();
  const state = useWrongSubsidiaryRedirect(experimentId, error, redirectTo);

  // Auto-switch path: backend pinned the experiment to an accessible subsidiary.
  if (state.status === 'redirecting') {
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <Loader2 aria-hidden="true" className="text-primary h-6 w-6 animate-spin" />
        <p className="text-muted-foreground text-sm leading-5">
          {t('detail.wrongSubsidiary.opening', { subsidiary: state.owner.name })}
        </p>
      </div>
    );
  }

  // Not-found / forbidden we couldn't auto-resolve: most likely a subsidiary
  // mismatch (the API can't tell us which one yet), so guide a manual switch
  // that preserves the deep link rather than dead-ending on "not found".
  const isScopeError =
    error instanceof ApiError && (error.code === 'NOT_FOUND' || error.code === 'FORBIDDEN');
  if (isScopeError) {
    return <WrongSubsidiaryFallback redirectTo={redirectTo} />;
  }

  // Anything else (5xx, network, unexpected) — honest, generic failure.
  return <p className="text-muted-foreground text-sm leading-5">{t('detail.loadError')}</p>;
}

/**
 * Recovery UI for a probable wrong-subsidiary error. When the user can access
 * other subsidiaries, it explains the experiment isn't in the current one and
 * lets them switch (reopening this same experiment URL afterwards). With no
 * other subsidiary to try, it degrades to an honest "not found" rather than a
 * misleading dead-end that claims the experiment lives elsewhere.
 */
function WrongSubsidiaryFallback({ redirectTo }: { redirectTo: string }) {
  const { t } = useTranslation('experiments');
  // Select stable references (the store array + a primitive id), then derive —
  // deriving inside the selector would return a new array every render and loop.
  const accessibleTenants = useAuthStore((s) => s.user?.accessibleTenants ?? EMPTY_TENANTS);
  const activeTenantId = useAuthStore((s) => s.user?.activeTenantId);
  const { switchTenant, isSwitching } = useTenantSwitch();

  const activeName = accessibleTenants.find((tenant) => tenant.id === activeTenantId)?.name;
  const otherTenants = useMemo(
    () => accessibleTenants.filter((tenant) => tenant.id !== activeTenantId),
    [accessibleTenants, activeTenantId],
  );

  // No sibling subsidiary to switch to — don't claim the experiment lives
  // elsewhere (misleading, and a dead-end with no action). Show an honest
  // "not found" instead. Covers single-subsidiary users and genuine 404s.
  if (otherTenants.length === 0) {
    return <p className="text-muted-foreground text-sm leading-5">{t('detail.notFound')}</p>;
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-lg leading-7 font-semibold">
          {t('detail.wrongSubsidiary.title')}
        </CardTitle>
        <CardDescription className="text-sm leading-5">
          {t('detail.wrongSubsidiary.body', { current: activeName ?? '—' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p className="text-foreground text-sm leading-5 font-semibold">
          {t('detail.wrongSubsidiary.switchPrompt')}
        </p>
        <div className="flex max-h-60 flex-col gap-2 overflow-auto">
          {otherTenants.map((tenant) => (
            <Button
              key={tenant.id}
              variant="outline"
              disabled={isSwitching}
              onClick={() => void switchTenant(tenant.id, { redirectTo })}
              className="cursor-pointer justify-start"
            >
              {tenant.name}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
